<template>
  <div>
    <v-fab-transition v-if="isModification">
      <v-btn v-hotkey="'ctrl+s'" color="green" @click="save" icon size="large" class="fab-fixed-bottom-right">
        <v-icon size="large">mdi-content-save-outline</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-container>
      <v-row>
        <v-col>
          <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error">
            {{ errorMessage }}
          </v-alert>
          <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning">
            Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
          </v-alert>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-switch v-model="section.topo_info" label="Activer la section Topographie"></v-switch>
          <small class="text-medium-emphasis"> (L'activation de la section sera sauvegardée automatiquement)</small>
        </v-col>
      </v-row>
      <v-row>
        <v-col v-if="uploadWaiting">
          <div class="text-center">
            <v-progress-circular :size="50" color="primary" indeterminate></v-progress-circular>
            <p class="text-blue">Veuillez patienter durant le téléchargement.</p>
          </div>
        </v-col>
        <v-col v-if="!uploadWaiting">
          <FileDrop :files="photosDropzone" accept=".jpg,.JPG" label multiple></FileDrop>
        </v-col>
      </v-row>
      <v-row v-if="photosDropzone.length !== 0">
        <v-col class="d-flex justify-center">
          <v-btn class="primary" @click="uploadPhotos()" :disabled="uploadWaiting" :loading="uploadWaiting">
            <v-icon start>mdi-cloud-upload</v-icon>
            Télécharger les images
          </v-btn>
        </v-col>
      </v-row>
      <v-form ref="sectionTopos" v-model="valid">
        <v-row>
          <v-col>
            <v-alert v-if="errorMessage" density="compact" variant="tonal" border="start" type="error">
              {{ errorMessage }}
            </v-alert>
            <v-alert v-if="isModification" density="compact" variant="tonal" border="start" type="warning">
              Modifications non sauvegardées. Sauvegardez (CTRL+S) avant de changer de section ou d'onglet.
            </v-alert>
          </v-col>
        </v-row>
        <v-row>
          <draggable v-model="topoImages" item-key="path" class="v-row" @change="handleChangeImages">
            <template #item="{ element: image, index: i }">
              <v-col cols="4">
                <v-card flat>
                  <v-img
                    :src="image.previewUrl"
                    class="text-white align-end"
                    gradient="to top, rgba(0,0,0,.6), rgba(0,0,0,0)"
                    max-height="300"
                    max-width="300"
                  >
                    <v-card-title class="mb-n3 ml-n2 text-body-small">{{ image.description }}</v-card-title>
                  </v-img>
                  <v-card-actions>
                    <v-text-field label="Description" v-model="image.description" class="ml-n4" clearable></v-text-field>
                    <v-btn icon color="error" @click="removeImage(i)">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-col>
            </template>
          </draggable>
        </v-row>
      </v-form>
    </v-container>
  </div>
</template>

<script>
  import draggable from "vuedraggable";
  import FileDrop from "@/components/FileDrop.vue";
  import { UploadService } from "@/store/modules/upload";
  import SectionsService from "@/store/modules/sections";
  import { SectionMediaService } from "@/services/SectionMedia.service";
  import { notify } from "@/services/Notification.service";

  export default {
    components: {
      FileDrop,
      draggable,
    },
    props: {
      section: {
        type: Object,
        default: () => ({}),
      },
      reloadData: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        photosDropzone: [],
        uploadWaiting: false,
        topoImages: [],
        isModification: false,
        errorMessage: "",
        valid: true,
      };
    },
    async created() {
      await this.getTopoImages();
    },
    async updated() {
      if (this.reloadData) {
        await this.getTopoImages();
        this.$emit("update", false);
      }
    },
    methods: {
      // Saved as { path, fileName, description } (no URL: issue #104). The previews are signed URLs that expire.
      async getTopoImages() {
        try {
          const topos = await SectionMediaService.getTopos(this.section.id);
          this.topoImages = topos.map(({ url, path, fileName, description }) => ({
            path,
            fileName,
            description,
            previewUrl: url,
          }));
        } catch (error) {
          console.error(error);
          this.errorMessage = "Impossible de charger les cartes topo.";
          this.topoImages = [];
        }
        this.watchImages();
      },
      // What sectionsTopos keeps: never the preview URL
      toposToSave() {
        return this.topoImages.map(({ path, fileName, description }) => ({ path, fileName, description: description || "" }));
      },
      watchImages() {
        this.topoImages.forEach((val) => {
          this.$watch(() => val, this.handleChangeImages, { deep: true });
        });
      },
      handleChangeImages() {
        this.errorMessage = "";
        this.isModification = true;
      },
      async uploadPhotos() {
        this.uploadWaiting = true;
        const folder = `topos/${this.section.id}`;
        const allOperations = this.photosDropzone.map((image) => UploadService.actions.uploadImage(folder, image));

        const allFiles = await Promise.all(allOperations).then((result) =>
          result.filter((res) => res.success).map((res) => ({ url: res.url, fileName: res.imageName })),
        );

        for (const file of allFiles) {
          this.topoImages.push({
            path: `${folder}/${file.fileName}`,
            fileName: file.fileName,
            description: "",
            previewUrl: file.url,
          });
        }

        await SectionsService.actions.setSectionsTopos(this.section.id, this.toposToSave());
        this.watchImages();
        this.photosDropzone = [];
        this.uploadWaiting = false;
      },
      removeImage(index) {
        const path = this.topoImages[index].path;
        const folder = path.slice(0, path.lastIndexOf("/"));
        UploadService.actions
          .deleteImage(folder, path.slice(path.lastIndexOf("/") + 1))
          .then(() => this.topoImages.splice(index, 1))
          .then(() => SectionsService.actions.setSectionsTopos(this.section.id, this.toposToSave()));
      },
      async save() {
        if (this.valid) {
          SectionsService.actions
            .setSectionsTopos(this.section.id, this.toposToSave())
            .then(() => {
              this.isModification = false;
              notify("La section a été sauvegardée avec succès.");
            })
            .catch((error) => {
              console.error(error);
              this.errorMessage =
                "Une erreur s'est produite lors de la sauvegarde des images, vérifiez les résultats et recommencez.";
            });
        } else {
          this.errorMessage = "Certains champs obligatoires ne répondent pas aux critères attendus, veuillez vérifier.";
        }
      },
    },
    watch: {
      "section.topo_info"() {
        SectionsService.actions.updateSection(this.section);
      },
      reloadData() {
        if (this.reloadData) {
          this.errorMessage = "";
          this.isModification = false;
        }
      },
    },
  };
</script>
